import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { supabase } from '@/config/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { CompleteOnboardingWizardOutput } from '@/types/onboarding';

/**
 * Onboarding Wizard - Step 5: Placeholder (Final Step)
 *
 * This is a placeholder component for Step 5 of the onboarding wizard.
 * This step includes the completion logic that marks onboarding as complete.
 * To be implemented with actual maternal grandparents form in Phase 3.
 */
export function Step5Placeholder() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [isCompleting, setIsCompleting] = useState(false);

  const handleBack = () => {
    navigate(ROUTES.ONBOARDING_STEP_4);
  };

  const handleComplete = async () => {
    if (!user) return;

    setIsCompleting(true);

    try {
      // Call the complete_onboarding_wizard RPC function
      // This will create the family tree and all persons/relationships
      const { data, error } = await supabase.rpc('complete_onboarding_wizard', {
        p_user_id: user.id,
      } as any);

      if (error) {
        console.error('Failed to complete onboarding:', error);
        toast.error(t('messages.completionError', 'Failed to complete onboarding'), {
          description: error.message,
        });
        return;
      }

      // Cast data to the correct type (RPC returns array with single object)
      const result = (data as unknown as CompleteOnboardingWizardOutput[])[0];

      // Log success with statistics
      console.log('Onboarding completed successfully:', result);

      // Refresh the user profile to get updated onboarding_completed_at
      await refreshProfile();

      toast.success(t('messages.completionSuccess', 'Onboarding completed successfully!'), {
        description: `Created ${result?.person_count || 0} people and ${result?.relationship_count || 0} relationships`,
      });

      // Redirect to dashboard
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error(t('messages.completionError', 'Failed to complete onboarding'));
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-6 py-12 md:px-20 lg:px-40">
      <div className="max-w-[800px] w-full flex flex-col items-center text-center space-y-8">
        {/* Header Section */}
        <div className="space-y-4">
          {/* Badge */}
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold tracking-wide uppercase">
            {t('step5.badge', 'Step 5 of 5')}
          </div>

          {/* Title */}
          <h1 className="text-slate-900 dark:text-slate-100 text-4xl md:text-5xl font-black leading-tight tracking-tight">
            {t('step5.title', 'Almost There!')}
          </h1>

          {/* Description */}
          <p className="text-slate-600 dark:text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            {t(
              'step5.description',
              'This is the final step. Click Complete to finish your onboarding and start building your family tree!'
            )}
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
          <Button
            onClick={handleBack}
            variant="outline"
            size="lg"
            className="flex-1 h-14 text-lg font-bold"
            disabled={isCompleting}
          >
            {t('common.back', 'Back')}
          </Button>
          <Button
            onClick={handleComplete}
            size="lg"
            className="flex-1 h-14 text-lg font-bold shadow-lg shadow-primary/20 active:scale-[0.98] transition-transform"
            disabled={isCompleting}
          >
            {isCompleting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('step5.completing', 'Completing...')}
              </>
            ) : (
              t('step5.complete', 'Complete Onboarding')
            )}
          </Button>
        </div>
      </div>
    </main>
  );
}
